import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Avatar, Icon, DogsInPill } from '../common';
import { SlotEditorPortal, editingFromEvent, type EditingSlot } from './SlotEditorPortal';
import { getNextSlot } from './shiftConstants';
import { isOnLeave, leaveByDate } from './leave';
import { monthGridWeeks, weekLabel, monthIsoOf, startOfWeek } from './week';
import type { Holiday, Shift, Staff } from './types';
import { DOW_ABBR, MONTHS_ABBR } from '../../lib/dates';


interface MonthViewProps {
  monthIso: string; // 'YYYY-MM'
  today: string; // ISO today
  staff: Staff[];
  shifts: Shift[];
  dogsByDate: Map<string, number>;
  holidays: Holiday[];
  filterStaffId: string | null;
  onAssign: (date: string, slot: number, staffId: string) => void;
  onRemove: (date: string, slot: number) => void;
  // Jump into the week grid for the clicked week. Omitted on a month-only page
  // (Rota) — the week header then isn't a link.
  onOpenWeek?: (mondayIso: string) => void;
  // Edit a day's leave from its on-leave marker (Rota Schedule lens).
  onEditLeave?: (h: Holiday) => void;
  // The month's anchor day. Marks which week-block is the scroll target; it does
  // NOT by itself cause a scroll (it defaults to today, and Rota must open at the
  // top — see the effect below).
  scrollToIso?: string;
  // A ONE-SHOT jump: the day an On-duty "view all" handed us. Non-null means
  // "scroll that day's week into view, once". Cleared by onJumpDone.
  jumpTo?: string | null;
  // Called once the jump has been performed, so the page can clear it.
  onJumpDone?: () => void;
  // Rendered at the foot of every week-block. A render-prop, not pay data threaded
  // through: MonthView stays about the ROSTER, and the caller composes whatever
  // belongs under a week (today, the hours & wages strip that replaced the Wages
  // lens). Omit it and the week-blocks render exactly as before.
  renderWeekFoot?: (weekDates: string[]) => ReactNode;
}

// The month roster — a stack of week-blocks (8 slots/day are too dense for a
// single calendar cell, so weeks read top to bottom). Each day shows its filled
// AM/PM slots compactly plus the dogs-in cue; a week header opens that week in
// the week grid. Markup matches the prototype's month layout so its styles
// apply, and editing reuses the shared slot editor (same per-period guard).
export function MonthView({
  monthIso,
  today,
  staff,
  shifts,
  dogsByDate,
  holidays,
  filterStaffId,
  onAssign,
  onRemove,
  onOpenWeek,
  onEditLeave,
  scrollToIso,
  jumpTo,
  onJumpDone,
  renderWeekFoot,
}: MonthViewProps) {
  const weeks = useMemo(() => monthGridWeeks(monthIso), [monthIso]);
  const leaveFor = useMemo(() => leaveByDate(holidays, weeks.flat()), [holidays, weeks]);

  // Scroll a week-block into view ONLY for a deliberate jump (Daily's On-duty
  // "view all"), never on a plain open.
  //
  // This used to key off [targetWeekStart, monthIso, scrollSignal] and so ran on
  // mount. `scrollToIso` is fed the app's shared anchor, and the anchor defaults to
  // TODAY — so merely opening Rota scrolled you down to today's week, past the
  // page's own heading.
  //
  // `jumpTo` is a ONE-SHOT: RotaPage sets it from the date-intent and clears it via
  // onJumpDone once consumed. It is deliberately not a counter — MonthView mounts
  // AFTER the intent fires (the month's data is still loading when the nav lands),
  // so any "has the number changed since I mounted?" scheme reads the bumped value
  // as its own baseline and never jumps. A null/non-null one-shot cannot be missed
  // that way, and cannot re-fire on a remount either.
  const targetWeekStart = jumpTo ? startOfWeek(jumpTo) : scrollToIso ? startOfWeek(scrollToIso) : null;
  const targetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!jumpTo) return;

    // Two frames, then scroll. A kept-alive page is display:none until this nav
    // makes it visible; one frame is too early — the just-shown slot hasn't laid
    // out, so scrollIntoView no-ops. The second frame runs after layout. Instant
    // so a follow-up re-render can't cut a smooth animation short.
    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        targetRef.current?.scrollIntoView({ block: 'start' });
        onJumpDone?.();
      });
    });
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
    };
  }, [jumpTo, onJumpDone]);
  const [editing, setEditing] = useState<EditingSlot | null>(null);

  const byDateSlot = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const s of shifts) m.set(s.date + '#' + s.slot, s.staffId);
    return m;
  }, [shifts]);

  const staffById = (id: string | null) => (id ? staff.find((s) => s.id === id) ?? null : null);
  const filledFor = (date: string): Set<number> => {
    const set = new Set<number>();
    for (let slot = 0; slot < 8; slot++) if (byDateSlot.get(date + '#' + slot)) set.add(slot);
    return set;
  };
  const openEditor = (e: React.MouseEvent, date: string, slot: number) =>
    setEditing(editingFromEvent(e, date, slot, byDateSlot.get(date + '#' + slot) ?? null));

  const renderMini = (date: string, slot: number) => {
    const staffId = byDateSlot.get(date + '#' + slot);
    if (!staffId) return null;
    const person = staffById(staffId);
    if (!person) return null;
    const dim = filterStaffId !== null && staffId !== filterStaffId;
    const conflict = isOnLeave(holidays, staffId, date);
    return (
      <button
        key={slot}
        type="button"
        className={'slot-mini ' + (slot < 4 ? 'am' : 'pm') + (dim ? ' dim' : '') + (conflict ? ' conflict' : '')}
        onClick={(e) => openEditor(e, date, slot)}
        title={conflict ? `${person.name} is on leave this day` : person.name}
      >
        <Avatar name={person.name} id={person.id} size={22} />
        <span className="name">{person.name.split(' ')[0]}</span>
      </button>
    );
  };

  const renderGroup = (date: string, period: 'am' | 'pm') => {
    const slots = period === 'am' ? [0, 1, 2, 3] : [4, 5, 6, 7];
    const next = getNextSlot(period, filledFor(date));
    return (
      <div className={'shift-group ' + period + '-group'}>
        <div className="group-head compact">
          <span className={'kind-stripe ' + period} />
          <span className="group-label">{period.toUpperCase()}</span>
        </div>
        {slots.map((s) => renderMini(date, s)).filter(Boolean)}
        {next !== null && (
          <button type="button" className="add-shift-btn" onClick={(e) => openEditor(e, date, next)} title="Add a shift">
            <span className="plus">+</span>
            <span className="lbl">Add shift</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="schedule month">
      <div className="schedule-main">
        {weeks.map((week, wi) => {
          const inThisWeek = week.includes(today);
          return (
            <div
              key={week[0]}
              ref={week[0] === targetWeekStart ? targetRef : undefined}
              className={'week-block' + (inThisWeek ? ' current' : '')}
            >
              <div
                className={'week-head' + (onOpenWeek ? ' clickable' : '')}
                {...(onOpenWeek
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      title: 'Open this week in week view',
                      onClick: () => onOpenWeek(week[0]!),
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenWeek(week[0]!);
                        }
                      },
                    }
                  : {})}
              >
                <div className="week-label">
                  <span className="num mono">Week {wi + 1}</span>
                </div>
                <div className="week-range mono">{weekLabel(week[0]!)}</div>
                {inThisWeek && <span className="tag-now">This week</span>}
                {onOpenWeek && (
                  <span className="week-open-cta">
                    Open <Icon name="right" size={12} />
                  </span>
                )}
              </div>

              <div className="day-row">
                {week.map((date) => {
                  const d = new Date(date + 'T00:00:00');
                  const outMonth = monthIsoOf(date) !== monthIso;
                  const isToday = date === today;
                  return (
                    <div
                      key={date}
                      className={'day-col density-names' + (isToday ? ' today' : '') + (outMonth ? ' out-month' : '')}
                    >
                      <div className="day-head">
                        <span className="dow">{DOW_ABBR[d.getDay()]}</span>
                        <span className="dnum mono">{d.getDate()}</span>
                        {outMonth && <span className="month-tag mono">{MONTHS_ABBR[d.getMonth()]}</span>}
                        <DogsInPill count={dogsByDate.get(date) ?? 0} showZero={false} />
                      </div>
                      {(() => {
                        const onLeave = (leaveFor.get(date) ?? []).filter((h) => !filterStaffId || h.staffId === filterStaffId);
                        if (onLeave.length === 0) return null;
                        const names = onLeave.map((h) => staffById(h.staffId)?.name.split(' ')[0] ?? '').join(', ');
                        return onEditLeave ? (
                          <button type="button" className="leave-strip" onClick={() => onEditLeave(onLeave[0]!)} title="Edit leave">
                            <span className="dot dot-hol" />
                            <span className="leave-names">{names} on leave</span>
                          </button>
                        ) : (
                          <div className="leave-strip">
                            <span className="dot dot-hol" />
                            <span className="leave-names">{names} on leave</span>
                          </div>
                        );
                      })()}
                      {renderGroup(date, 'am')}
                      {renderGroup(date, 'pm')}
                    </div>
                  );
                })}
              </div>

              {renderWeekFoot?.(week)}
            </div>
          );
        })}
      </div>

      <SlotEditorPortal
        editing={editing}
        staff={staff}
        shifts={shifts}
        holidays={holidays}
        onAssign={onAssign}
        onRemove={onRemove}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}
